import React from 'react';
import { 
  FaBus,
  FaShieldAlt,
  FaClock,
  FaMapMarkedAlt,
  FaBusAlt,
  FaMapMarkerAlt,
  FaUserShield,
  FaVideo,
  FaMedkit,
  FaIdCard,
  FaCheckCircle,
  FaWrench,
  FaBell
} from 'react-icons/fa';
import TransportImage from '../../assets/images/section/transport.webp'

const TransportService = () => {
  const stats = [
    { icon: FaBus, value: "12", label: "Modern Buses", emoji: "🚌" },
    { icon: FaShieldAlt, value: "100%", label: "Safety Record", emoji: "🛡️" },
    { icon: FaClock, value: "7AM-5PM", label: "Service Hours", emoji: "⏰" },
    { icon: FaMapMarkedAlt, value: "Wide", label: "City Coverage", emoji: "🗺️" }
  ];

  const features = [
    {
      icon: FaBusAlt,
      bgColor: "bg-blue-100",
      textColor: "text-blue-600",
      title: "Modern Fleet",
      description: "Well-maintained, air-conditioned buses with comfortable seating"
    },
    {
      icon: FaMapMarkerAlt,
      bgColor: "bg-emerald-100",
      textColor: "text-emerald-600",
      title: "GPS Tracking",
      description: "Real-time location tracking with parental app access"
    },
    {
      icon: FaUserShield,
      bgColor: "bg-purple-100",
      textColor: "text-purple-600",
      title: "Trained Attendants",
      description: "Female attendants ensuring safety of younger students"
    },
    {
      icon: FaVideo,
      bgColor: "bg-amber-100",
      textColor: "text-amber-600",
      title: "CCTV Surveillance",
      description: "24/7 monitoring with live feed access for parents"
    },
    {
      icon: FaMedkit,
      bgColor: "bg-red-100",
      textColor: "text-red-600",
      title: "Medical Safety",
      description: "First-aid kits and trained staff for emergencies"
    },
    {
      icon: FaIdCard,
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-600",
      title: "Verified Drivers",
      description: "Background checked, experienced drivers with training"
    }
  ];

  const safetyItems = [
    { icon: FaShieldAlt, text: "Speed governors" },
    { icon: FaWrench, text: "Regular maintenance" },
    { icon: FaMedkit, text: "First aid equipped" },
    { icon: FaBell, text: "Parent notification" }
  ];

  return (
    <section className="py-12 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium text-blue-600 rounded-full bg-blue-50">
            <FaBus className="w-3 h-3 mr-1.5" />
            Transport Service
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">Safe & Reliable School Transport</h2>
          <p className="text-sm text-gray-600">Ensuring safe and comfortable transportation for every student</p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Left Column - Image and Stats */}
          <div className="relative">
            {/* Image */}
            <div className="overflow-hidden shadow-xl rounded-2xl">
              <img 
                alt="Modern School Bus Transport" 
                className="object-cover w-full h-96" 
                src={TransportImage}
              />
            </div>

            {/* Stats Overlay */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="p-4 border bg-white/90 backdrop-blur-sm rounded-xl border-white/20">
                <div className="grid grid-cols-4 gap-3">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="mb-1 text-2xl">{stat.emoji}</div>
                      <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                      <div className="text-xs text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Features */}
          <div>
            {/* Transport Features */}
            <div className="p-5 mb-6 border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Transport Features</h3>
              <p className="mb-4 text-sm text-gray-600">
                Our transport service ensures maximum safety, comfort, and reliability with state-of-the-art facilities and trained personnel.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="p-3 bg-white border border-gray-100 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className={`p-2 rounded-md ${feature.bgColor} ${feature.textColor}`}>
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="mb-1 text-sm font-semibold text-gray-900">{feature.title}</h4>
                        <p className="text-xs leading-relaxed text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Safety Assurance */}
            <div className="p-5 border bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border-emerald-100">
              <div className="flex items-center gap-2 mb-4">
                <FaShieldAlt className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">Safety Assurance</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {safetyItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <FaCheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransportService;